import { type ComputedRef, computed, onUnmounted, type Ref, ref, watch } from 'vue'
import {
    api,
    type CeoQuestionAnswer,
    type ChatDetail,
    type GateDecision,
    type GateRow,
    PLAN_STATUS_AWAITING_CEO,
    type PlanDetail,
    type PlanItem,
    type PlanRow,
    type RunDetail,
    type RunRow,
} from '@/api'
import { requireAiAccount } from '@/lib/aiAccountGuard'
import { toolRunId } from '@/lib/chat'
import { clearGateDecisionDraft } from '@/lib/gateDecisionDraft'

/**
 * Where the proposal born of a Create task conversation stands (#245), as the page shows it:
 *
 * - `none`: nothing has been handed over to draft yet (or the conversation has no plan at all).
 * - `drafting`: the proposing run is at work, or a later round is after Request changes.
 * - `proposal`: the plan's sign-off question is waiting on the person.
 * - `applying`: decided here, or approved, and the plan has not caught up yet.
 * - `created`: the plan was applied. `rejected`: it was rejected. `failed`: it could not be applied.
 * - `handed_off`: approved, but the plan is not a change the OS applies itself (its kind is not `os_change`, #236),
 *   so the approval creates nothing and the plan stays `approved` for good.
 * - `stopped`: the drafting run stopped (failed, cancelled or blocked) before a question existed, or a later round
 *   after Request changes ended without writing its revision, or never started at all.
 */
export type ProposalStage =
    | 'none'
    | 'drafting'
    | 'proposal'
    | 'applying'
    | 'created'
    | 'handed_off'
    | 'rejected'
    | 'failed'
    | 'stopped'

/** Stages nothing moves on from by itself: the page stops reading the plan, and looks for a newer one only when the conversation changes. */
const SETTLED_STAGES: ReadonlySet<ProposalStage> = new Set(['created', 'handed_off', 'rejected', 'failed'])

/** The tool a `create_task` conversation hands its agreed brief over with: the run it starts drafts the proposal. */
export const HAND_OFF_TASK_BRIEF = 'os.hand_off_task_brief'

/** How often the page reads the plan again while it follows it. */
export const PROPOSAL_FOLLOW_MS = 2000

/**
 * The one kind of plan the OS applies itself, so the only one an approval moves on from `approved`. The `create_task`
 * designer is told to write this kind, but `os.write_plan` takes any kind it is given, and the plan bridge leaves an
 * approved plan of another kind `approved` for good (`plan.apply_deferred`, `not_an_org_change`): the page says so
 * (`handed_off`) rather than wait on an apply that never comes.
 */
const ORG_CHANGE_KIND = 'os_change'

/** A plan sent back with Request changes, until its later round writes the revision. */
const CHANGES_REQUESTED = 'changes_requested'

/** The event a run records for a successor it started. */
const SUCCESSOR_STARTED = 'successor.started'

/** The events a run records for a successor it could not start: refused (`no_assignee`, …), or its task is gone. */
const SUCCESSOR_NOT_STARTED = 'successor.not_started'
const SUCCESSOR_MISSING = 'successor.missing'

/** Every event that says what became of a successor: the hops a later round is read from. */
const SUCCESSOR_HOPS: ReadonlySet<string> = new Set([SUCCESSOR_STARTED, SUCCESSOR_NOT_STARTED, SUCCESSOR_MISSING])

/**
 * The events a run records for the new run its work carries on in (`payload.retryRunId`): resumed once its block
 * cleared (`RunsService.resumeBlocked`, which leaves it `failed`), retried (which cancels a blocked run) or recovered
 * after its worker was lost (which leaves it `failed`).
 */
const CONTINUATIONS: ReadonlySet<string> = new Set(['run.resumed', 'run.retried', 'run.recovered'])

/** How many continuations one read follows at most, so a chain that loops can never hold the page. */
const MAX_CONTINUATIONS = 10

/** What a continuation leaves the run it continues: the statuses worth asking whether its work carried on. */
const CONTINUED_FROM: ReadonlySet<string> = new Set(['failed', 'cancelled'])

/** What became of a decision made on this page: the decision the API recorded, or `refused` as no longer waiting. */
export type DecisionHere = GateDecision | 'refused'

/**
 * After Request changes, a later round the OS never started: the asking run's successor on its own task was refused
 * (`successor.not_started`) or its task is gone (`successor.missing`). `reason` is the OS's own word for why
 * (`no_assignee`, `task_not_runnable`, `task_missing`, …), so the page shows it under Details.
 */
export type UnstartedRound = { status: 'not_started'; reason: string }

/**
 * The run at work that the plan does not name: before there is a plan, the run the brief was handed to; after Request
 * changes, the later round's (or the round that never started); otherwise the run the plan's own drafting run carried
 * on in. Each is the run as its work stands now, after any continuation (`continuationOf`).
 */
export type UnnamedRun = RunRow | UnstartedRound

/** A later round as the run that asked recorded it: the run it started, or why none started. */
export type LaterRoundHop = { runId: string } | UnstartedRound

/**
 * A run that will do no more drafting by itself, or a later round that never started (`not_started`). `blocked` can
 * carry on once unblocked, so it is still followed.
 */
const STOPPED_RUN_STATUSES: ReadonlySet<string> = new Set(['failed', 'cancelled', 'blocked', 'not_started'])

/** A run that has ended, whatever became of it. */
const ENDED_RUN_STATUSES: ReadonlySet<string> = new Set(['succeeded', 'failed', 'cancelled'])

/** The gate the API refused a decision on because it is no longer waiting: decided or closed somewhere else. */
const GATE_NOT_WAITING = 'gate_not_waiting'

/** The task an applied plan created, as the confirmation names it and Open task opens it. */
export type CreatedTask = { id: string; name: string }

/** One item an apply stopped on, and why. */
export type FailedItem = { handle: string; label: string; reason: string }

function text(value: unknown): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

function itemOf(plan: PlanDetail, handle: string): PlanItem | undefined {
    return (plan.items ?? []).find(item => item.handle === handle)
}

/** What an item is called: its name, else its slug, else plain words. Its handle is the plan's own and never shown. */
function itemLabel(item: PlanItem | undefined): string {
    return text(item?.name) ?? text(item?.slug) ?? 'An item with no name'
}

/** The task an applied plan created, or null when it reports none. */
export function createdTaskOf(plan: PlanDetail | null): CreatedTask | null {
    const result = (plan?.itemResults ?? []).find(
        each => each.type === 'create_task' && each.outcome === 'applied' && text(each.id) !== null,
    )
    if (!plan || !result?.id) {
        return null
    }
    return { id: result.id, name: text(itemOf(plan, result.handle)?.name) ?? 'Your new task' }
}

/** The items the apply stopped on, each with the reason the apply recorded. */
export function failedItemsOf(plan: PlanDetail | null): FailedItem[] {
    if (!plan) {
        return []
    }
    return (plan.itemResults ?? [])
        .filter(each => each.outcome === 'failed')
        .map(each => ({
            handle: each.handle,
            label: itemLabel(itemOf(plan, each.handle)),
            reason: text(each.error) ?? 'No reason was recorded.',
        }))
}

/**
 * Why drafting stopped, from the run that was drafting, with the reason the run recorded when it recorded one. The
 * recorded reason is the system's own words, so the page shows this under Details, after `stopSummaryOf`.
 */
export function stopReasonOf(run: Pick<RunRow, 'status' | 'failedReason' | 'blockedReason'> | UnstartedRound): string {
    if ('reason' in run) {
        return `Drafting the new version did not start: ${run.reason}`
    }
    if (run.status === 'succeeded') {
        return 'Drafting ended without a revised version to approve.'
    }
    if (run.status === 'failed') {
        const reason = text(run.failedReason)
        return reason ? `Drafting stopped: ${reason}` : 'Drafting stopped: the run drafting the proposal failed.'
    }
    if (run.status === 'blocked') {
        const reason = text(run.blockedReason)
        return reason
            ? `Drafting is on hold: ${reason}`
            : 'Drafting is on hold until what blocks the run drafting the proposal is cleared.'
    }
    return 'Drafting stopped: the run drafting the proposal was cancelled.'
}

/** What happened to drafting, in plain words and no system text: the line the page leads with when it stops. */
export function stopSummaryOf(run: Pick<RunRow, 'status'> | UnstartedRound): string {
    if ('reason' in run) {
        return 'Drafting the new version could not start, so no task was created.'
    }
    if (run.status === 'succeeded') {
        return 'Drafting ended without a new version to approve, so no task was created.'
    }
    if (run.status === 'blocked') {
        return 'Drafting is on hold. It carries on here once what holds it up is sorted.'
    }
    if (run.status === 'cancelled') {
        return 'Drafting was cancelled before there was anything to approve, so no task was created.'
    }
    return 'Drafting stopped before there was anything to approve, so no task was created.'
}

/**
 * The run drafting the plan, while the plan has no question waiting and that run is the one at work on it: a plan
 * still `drafting`, or awaiting the person before its run has asked. After Request changes the later round is a
 * run the plan does not name yet: it is read apart (`unnamedRun`), off the run that asked (`laterRoundHop`).
 */
function draftingRunOf(plan: PlanDetail): RunRow | null {
    return plan.status === 'drafting' || plan.status === PLAN_STATUS_AWAITING_CEO ? (plan.run ?? null) : null
}

/**
 * The run whose progress the page reads: before there is a plan, the run the brief was handed to; after Request
 * changes, the later round's run (or the round that never started), which the plan names only once it writes;
 * otherwise the plan's own drafting run, or the run that carries its work on once that run was resumed or retried.
 */
function runAtWork(plan: PlanDetail | null, unnamedRun: UnnamedRun | null): UnnamedRun | null {
    return !plan || plan.status === CHANGES_REQUESTED ? unnamedRun : (unnamedRun ?? draftingRunOf(plan))
}

/** The stage a plan's own status says, whatever its gate and its run. */
const SETTLED_PLAN_STAGES: Readonly<Record<string, ProposalStage>> = {
    applied: 'created',
    rejected: 'rejected',
    error: 'failed',
    approved: 'applying',
}

/**
 * After Request changes, until the later round revises the plan: drafting while that round's run is on its way or at
 * work, else why it stopped. A round whose run ended, however, without writing the revision has stopped too: it left
 * the plan `changes_requested`, and nothing else will move it. So has a round the OS never started.
 */
function laterRoundStage(run: UnnamedRun | null): ProposalStage {
    return run && (STOPPED_RUN_STATUSES.has(run.status) || ENDED_RUN_STATUSES.has(run.status)) ? 'stopped' : 'drafting'
}

/** Before there is a plan: drafting while the run the brief was handed to works on it, or why it stopped. */
function handOffStage(run: UnnamedRun | null): ProposalStage {
    if (!run) {
        return 'none'
    }
    if (STOPPED_RUN_STATUSES.has(run.status)) {
        return 'stopped'
    }
    return ENDED_RUN_STATUSES.has(run.status) ? 'none' : 'drafting'
}

/**
 * Where the proposal stands, from the plan the conversation owns, the run at work that the plan does not name
 * (`unnamedRun`: before there is a plan, the run its brief was handed to; after Request changes, the later round's
 * run, or the round that never started) and the gates decided on this page whose decision the plan has not caught up
 * with yet.
 */
export function proposalStage(
    plan: PlanDetail | null,
    unnamedRun: UnnamedRun | null,
    decidedHere: Pick<ReadonlySet<string>, 'has'> = new Set(),
): ProposalStage {
    if (!plan) {
        return handOffStage(unnamedRun)
    }
    if (plan.status === 'approved' && plan.kind !== ORG_CHANGE_KIND) {
        return 'handed_off'
    }
    if (plan.status === CHANGES_REQUESTED) {
        return laterRoundStage(unnamedRun)
    }
    const settled = SETTLED_PLAN_STAGES[plan.status]
    if (settled) {
        return settled
    }
    if (failedItemsOf(plan).length > 0) {
        return 'failed'
    }
    const gate = plan.status === PLAN_STATUS_AWAITING_CEO ? (plan.openGate ?? null) : null
    if (gate) {
        return decidedHere.has(gate.id) ? 'applying' : 'proposal'
    }
    const run = runAtWork(plan, unnamedRun)
    return run && STOPPED_RUN_STATUSES.has(run.status) ? 'stopped' : 'drafting'
}

/** Whether the page keeps reading the plan again: until it is settled, or its drafting has ended for good. */
function stillMoving(plan: PlanDetail | null, unnamedRun: UnnamedRun | null, stage: ProposalStage): boolean {
    if (stage === 'none' || SETTLED_STAGES.has(stage)) {
        return false
    }
    if (stage === 'stopped') {
        return runAtWork(plan, unnamedRun)?.status === 'blocked'
    }
    return true
}

/**
 * The conversation's newest plan. Every round revises the same row, but an applied plan is followed by the next one,
 * never revised (#2127): a run that writes again in the conversation after an apply starts a new row.
 */
function newest(rows: readonly PlanRow[]): PlanRow | null {
    return [...rows].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] ?? null
}

/** The run the conversation's brief was handed to, from the newest hand-off in it, or null before there is one. */
export function handOffRunId(chat: ChatDetail | null): string | null {
    const rows = (chat?.messages ?? []).filter(row => row.toolName === HAND_OFF_TASK_BRIEF && toolRunId(row))
    const last = rows[rows.length - 1]
    return last ? toolRunId(last) : null
}

/**
 * After Request changes, the later round as the run that asked recorded it: the newest hop it recorded to a run of its
 * own task (its self-successor on `ceo_changes_requested`), or why that successor never started. Read off the run's
 * own events, never its `flow.next`: run detail's workflow leaves out every OS task (`RunsService.flow`), and
 * `task-create` is one. Null while the run that asked has recorded no such hop yet, or its task is not known.
 */
export function laterRoundHop(asked: Pick<RunDetail, 'task' | 'events'> | null | undefined): LaterRoundHop | null {
    const slug = text(asked?.task?.slug)
    if (!slug) {
        return null
    }
    const hops = (asked?.events ?? []).filter(
        event => SUCCESSOR_HOPS.has(event.type) && event.payload?.['slug'] === slug,
    )
    const last = hops[hops.length - 1]
    if (!last) {
        return null
    }
    if (last.type === SUCCESSOR_STARTED) {
        const runId = text(last.payload?.['runId'])
        return runId ? { runId } : null
    }
    const reason = text(last.payload?.['reason']) ?? (last.type === SUCCESSOR_MISSING ? 'task_missing' : 'start_failed')
    return { status: 'not_started', reason }
}

/**
 * The run a run's work carries on in: the newest `run.resumed`, `run.retried` or `run.recovered` it recorded, or null
 * when it recorded none. The OS continues a run as a new run and leaves the old one failed or cancelled, so the old
 * run's own status is not where its work stands.
 */
export function continuationOf(detail: Pick<RunDetail, 'events'> | null | undefined): string | null {
    const hops = (detail?.events ?? []).filter(
        event => CONTINUATIONS.has(event.type) && text(event.payload?.['retryRunId']) !== null,
    )
    const last = hops[hops.length - 1]
    return last ? text(last.payload?.['retryRunId']) : null
}

/** A decision the page tried to record and could not, and the gate it was on. */
type DecisionFailure = { gateId: string; message: string; refused: boolean }

export type CreateTaskProposal = {
    stage: ComputedRef<ProposalStage>
    plan: Ref<PlanDetail | null>
    /** The sign-off question waiting on the person, while the stage is `proposal`. */
    gate: ComputedRef<GateRow | null>
    createdTask: ComputedRef<CreatedTask | null>
    failedItems: ComputedRef<FailedItem[]>
    /** Why drafting stopped, with the reason the run recorded, while the stage is `stopped`: shown under Details. */
    stopReason: ComputedRef<string | null>
    /** What happened to drafting, in plain words, while the stage is `stopped`. */
    stopSummary: ComputedRef<string | null>
    /**
     * While the stage is `applying`, what it waits on: the decision recorded on this page (or an approval made
     * anywhere), or `refused` when the API said the question was already decided somewhere else.
     */
    applyingFor: ComputedRef<DecisionHere | null>
    /** A decision is on its way to the API. */
    deciding: Ref<boolean>
    /** The decision on its way, so the card can show it while it is recorded. */
    decided: ComputedRef<GateDecision | undefined>
    /**
     * Why the last decision was not recorded. A plain refusal shows only above the question it was about; one refused
     * as already decided elsewhere shows wherever the person lands, until another round's question opens.
     */
    error: ComputedRef<string | null>
    decide: (
        gateId: string,
        decision: GateDecision,
        note: string,
        answers?: CeoQuestionAnswer[],
        edit?: Record<string, unknown>,
        choice?: string,
    ) => Promise<void>
    /** Reads the plan again now. */
    refresh: () => Promise<void>
}

/**
 * The proposal a Create task conversation leads to (#245), followed on the page. The plan is found through the chat
 * session that owns it, or, before the session's plan is found, through the run the brief was handed to
 * (`producedByRunId`, #371): every later round after Request changes revises the same plan row, so a later round's
 * question is found exactly as the first. The plan is read again every `PROPOSAL_FOLLOW_MS` while it is still moving
 * (drafting, waiting on a decision, being applied), so a decision made on Gates shows here at the next read.
 *
 * Between Request changes and the later round's revision the plan names no run at work, so the later round is read
 * off the events of the run that asked (`laterRoundHop`): a round that stops before it revises the plan, or that the
 * OS never starts, shows why, as the first round does. Every run the page reads is followed into the run its work
 * carried on in (`continuationOf`): a round that was resumed once its block cleared, retried or recovered after a lost
 * worker is still drafting, not stopped. Once the followed plan has settled, a change in the
 * conversation looks for the conversation's newest plan again, so a plan written after an applied one (#2127) is
 * followed in its place.
 *
 * The sign-off question is decided as Gates and the plan page decide it (`api.decideGate`), and the plan is read
 * again at once. Nothing is followed while `active` says the conversation is not on the page.
 */
export function useCreateTaskProposal(conversation: Ref<ChatDetail | null>, active: () => boolean): CreateTaskProposal {
    const plan = ref<PlanDetail | null>(null)
    /** The run at work that the plan does not name: the hand-off run before a plan, the later round's after one. */
    const unnamedRun = ref<UnnamedRun | null>(null)
    const deciding = ref(false)
    /** The decision on its way to the API, and the gate it is on. */
    const pending = ref<{ gateId: string; decision: GateDecision } | null>(null)
    /** Gates decided (or refused) on this page: never offered again, whatever a read that has not caught up says. */
    const decidedHere = ref<ReadonlyMap<string, DecisionHere>>(new Map())
    const failure = ref<DecisionFailure | null>(null)
    let timer: ReturnType<typeof setTimeout> | null = null
    let latest = 0
    let gone = false
    let followedId: string | null = null
    /**
     * The later round the run that asked recorded, once it has: that run has ended, so its hop never changes, and
     * only the later round's own run is read again after it.
     */
    let laterHop: { askedBy: string; hop: LaterRoundHop } | null = null
    /** For each run the page reads, the newest run its work was found carrying on in: later reads start there. */
    const carriedOn = new Map<string, string>()

    const stage = computed(() => proposalStage(plan.value, unnamedRun.value, decidedHere.value))
    const gate = computed(() => (stage.value === 'proposal' ? (plan.value?.openGate ?? null) : null))
    const createdTask = computed(() => (stage.value === 'created' ? createdTaskOf(plan.value) : null))
    const failedItems = computed(() => (stage.value === 'failed' ? failedItemsOf(plan.value) : []))
    const stoppedRun = computed(() => (stage.value === 'stopped' ? runAtWork(plan.value, unnamedRun.value) : null))
    const stopReason = computed(() => (stoppedRun.value ? stopReasonOf(stoppedRun.value) : null))
    const stopSummary = computed(() => (stoppedRun.value ? stopSummaryOf(stoppedRun.value) : null))
    const applyingFor = computed<DecisionHere | null>(() => {
        if (stage.value !== 'applying') {
            return null
        }
        const open = plan.value?.status === PLAN_STATUS_AWAITING_CEO ? plan.value.openGate?.id : undefined
        return (open && decidedHere.value.get(open)) || 'approve'
    })
    const decided = computed(() =>
        pending.value && pending.value.gateId === gate.value?.id ? pending.value.decision : undefined,
    )
    const error = computed(() => {
        const failed = failure.value
        if (!failed) {
            return null
        }
        const open = gate.value?.id ?? null
        return open === failed.gateId || (failed.refused && open === null) ? failed.message : null
    })

    function stopFollowing(): void {
        if (timer !== null) {
            clearTimeout(timer)
            timer = null
        }
    }

    function followLater(): void {
        stopFollowing()
        if (!gone) {
            timer = setTimeout(() => void refresh(), PROPOSAL_FOLLOW_MS)
        }
    }

    async function findPlan(chat: ChatDetail): Promise<PlanRow | null> {
        const bySession = newest(await api.plans({ chatSessionId: chat.id }))
        if (bySession) {
            return bySession
        }
        const runId = handOffRunId(chat)
        return runId ? newest(await api.plans({ producedByRunId: runId })) : null
    }

    /** The plan to read: the one followed while it can still move, else the conversation's newest. */
    async function planToRead(chat: ChatDetail): Promise<string | null> {
        const followed = plan.value
        if (followed && !SETTLED_STAGES.has(stage.value)) {
            return followed.id
        }
        return (await findPlan(chat))?.id ?? followed?.id ?? null
    }

    /** Follows `detail`'s continuations from run `id`, `steps` at most: the last run reached, and its detail. */
    async function carryOn(
        id: string,
        detail: RunDetail | null | undefined,
        steps: number,
    ): Promise<{ id: string; detail: RunDetail | null | undefined }> {
        const next = continuationOf(detail)
        if (!next || next === id || steps <= 0) {
            return { id, detail }
        }
        return carryOn(next, await api.run(next), steps - 1)
    }

    /**
     * Run `runId` as its work stands now: that run, or the newest run it carried on in once it was resumed, retried
     * or recovered (`continuationOf`), a few steps at most. The run reached is kept, so the next read starts there
     * rather than walking the chain again.
     */
    async function latestRun(runId: string): Promise<RunRow | null> {
        const from = carriedOn.get(runId) ?? runId
        const reached = await carryOn(from, await api.run(from), MAX_CONTINUATIONS)
        if (reached.id !== runId) {
            carriedOn.set(runId, reached.id)
        }
        return reached.detail?.run ?? null
    }

    /**
     * After Request changes, the later round: the run the asking run started on its own task, as its work stands now,
     * or the round the OS never started. Null while the asking run has recorded neither. The run that asked is the
     * one the plan names, or the run its work carried on in: a run resumed or recovered restores the conversation, the
     * plan write included, so it can ask without writing again, and its question is offered on the plan all the same
     * (`PlansService.get`). Its later round is then that run's successor, so it is read there.
     */
    async function laterRoundRun(read: PlanDetail): Promise<UnnamedRun | null> {
        const askedBy = read.producedByRunId
        let hop = laterHop?.askedBy === askedBy ? laterHop.hop : null
        if (!hop) {
            hop = laterRoundHop((await carryOn(askedBy, await api.run(askedBy), MAX_CONTINUATIONS)).detail)
            laterHop = hop ? { askedBy, hop } : null
        }
        if (!hop) {
            return null
        }
        return 'runId' in hop ? latestRun(hop.runId) : hop
    }

    /**
     * The run at work that a plan read does not name: after Request changes, the later round's; else, once the plan's
     * own drafting run failed or was cancelled, the run its work carried on in, if it did. That run names itself on
     * the plan only once it writes. A continuation that cannot be read leaves the plan's own run as the page knows it.
     */
    async function unnamedRunOf(read: PlanDetail): Promise<UnnamedRun | null> {
        if (read.status === CHANGES_REQUESTED) {
            return laterRoundRun(read)
        }
        const own = draftingRunOf(read)
        if (!own || !CONTINUED_FROM.has(own.status)) {
            return null
        }
        const now = await latestRun(own.id).catch(() => null)
        return now && now.id !== own.id ? now : null
    }

    /** The plan as it stands now, with the run at work it does not name: the hand-off's, or the later round's. */
    async function readProposal(chat: ChatDetail): Promise<{ read: PlanDetail | null; run: UnnamedRun | null }> {
        const id = await planToRead(chat)
        if (id) {
            const read = await api.getPlan(id)
            return { read, run: await unnamedRunOf(read) }
        }
        const runId = handOffRunId(chat)
        return { read: null, run: runId ? await latestRun(runId) : null }
    }

    async function refresh(): Promise<void> {
        stopFollowing()
        const chat = conversation.value
        if (gone || !chat || !active()) {
            return
        }
        const mine = ++latest
        let read: PlanDetail | null
        let run: UnnamedRun | null
        try {
            ;({ read, run } = await readProposal(chat))
        } catch {
            // A read that failed is tried again while there is a proposal to follow: as far as the page knows, it is
            // still moving. Before one is found, the conversation's next change looks again.
            if (mine === latest && (plan.value || unnamedRun.value)) {
                followLater()
            }
            return
        }
        if (mine !== latest || gone || conversation.value?.id !== chat.id) {
            return
        }
        took(read, run)
    }

    /** Keeps what a read found, and follows on while it can still move. */
    function took(read: PlanDetail | null, run: UnnamedRun | null): void {
        if (plan.value && read && read.id !== plan.value.id) {
            // A newer plan in the same conversation: what was refused on the old one says nothing about it.
            failure.value = null
        }
        plan.value = read
        unnamedRun.value = run
        if (stillMoving(read, run, stage.value)) {
            followLater()
        }
    }

    function decidedOnPage(gateId: string, how: DecisionHere): void {
        decidedHere.value = new Map([...decidedHere.value, [gateId, how]])
    }

    async function decide(
        gateId: string,
        decision: GateDecision,
        note: string,
        answers?: CeoQuestionAnswer[],
        edit?: Record<string, unknown>,
        choice?: string,
    ): Promise<void> {
        if (deciding.value || gate.value?.id !== gateId) {
            return
        }
        deciding.value = true
        failure.value = null
        // #1796: a decision resumes the run that asked, which is model work, so it asks the guard first.
        if (!(await requireAiAccount())) {
            deciding.value = false
            return
        }
        pending.value = { gateId, decision }
        try {
            await api.decideGate(gateId, decision, note, answers, edit, choice)
        } catch (caught) {
            pending.value = null
            deciding.value = false
            if (caught instanceof Error && caught.message === GATE_NOT_WAITING) {
                decidedOnPage(gateId, 'refused')
                failure.value = {
                    gateId,
                    refused: true,
                    message: 'This was already decided somewhere else, so your decision was not recorded.',
                }
                await refresh()
                return
            }
            failure.value = { gateId, refused: false, message: 'Your decision was not recorded. Try again.' }
            return
        }
        clearGateDecisionDraft(gateId)
        decidedOnPage(gateId, decision)
        pending.value = null
        deciding.value = false
        await refresh()
    }

    /** A conversation of its own is a proposal of its own: Start over and Create another begin with none. */
    function reset(): void {
        stopFollowing()
        latest += 1
        plan.value = null
        unnamedRun.value = null
        pending.value = null
        decidedHere.value = new Map()
        failure.value = null
        laterHop = null
        carriedOn.clear()
    }

    watch(
        () => {
            const chat = conversation.value
            return chat && active() ? `${chat.id}\u0000${chat.status}\u0000${chat.messages.length}` : null
        },
        key => {
            if (key === null) {
                stopFollowing()
                return
            }
            const id = conversation.value?.id ?? null
            if (id !== followedId) {
                followedId = id
                reset()
            }
            void refresh()
        },
        { immediate: true },
    )

    onUnmounted(() => {
        gone = true
        stopFollowing()
    })

    return {
        stage,
        plan,
        gate,
        createdTask,
        failedItems,
        stopReason,
        stopSummary,
        applyingFor,
        deciding,
        decided,
        error,
        decide,
        refresh,
    }
}
